import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { type TFunction } from 'i18next';
import { useLocalStorage } from 'usehooks-ts';

import BriefcaseIcon from '@/assets/icons/briefcase.svg?react';
import CollapseButton from '@/components/CollapseButton';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useBodyClass } from '@/hooks/useBodyClass';
import { useCssVariables } from '@/hooks/useCssVariables';
import { useDirection } from '@/hooks/useDirection';
import { ROUTER_PATHS } from '@/root/linker.ts';
import { navbar, NAVBAR_FULL_WIDTH_CSS_VAR as NAVBAR_FULL_WIDTH_CSS_VAR, transitions } from '@/theme/constants';
import typography from '@/theme/typography';

import MembersIcon from './assets/members.svg?react';
import OrganizationsIcon from './assets/organizations.svg?react';
import RolesIcon from './assets/roles.svg?react';
import TalentsIcon from './assets/talents.svg?react';

type MenuItem = {
  title: string;
  Icon: React.ComponentType;
  to: string;
};

const getMenuItems = (t: TFunction, isMidotUser: boolean) => {
  const rolesItem: MenuItem = {
    title: t('layouts.navbar.roles'),
    Icon: RolesIcon,
    to: ROUTER_PATHS.ROLES
  };

  const items = [
    {
      title: t('layouts.navbar.organizations'),
      Icon: OrganizationsIcon,
      to: ROUTER_PATHS.ORGANIZATIONS
    },
    {
      title: t('layouts.navbar.processes'),
      Icon: BriefcaseIcon,
      to: ROUTER_PATHS.PROCESSES
    },
    {
      title: t('layouts.navbar.talentsPool'),
      Icon: TalentsIcon,
      to: ROUTER_PATHS.TALENTS
    },
    {
      title: t('layouts.navbar.members'),
      Icon: MembersIcon,
      to: ROUTER_PATHS.USERS
    },
    isMidotUser && rolesItem
  ].filter((item: unknown): item is MenuItem => Boolean(item));

  return items;
};

const Navbar = () => {
  const [isNavbarOpen, setOpen] = useLocalStorage('navbarOpen', true);
  const { t, i18n } = useTranslation('common');
  const { start, end, applyDirectionalStyles } = useDirection();
  const { isMidotUser } = useIsMidotUser();
  const menuItems = useMemo(() => getMenuItems(t, isMidotUser), [t, isMidotUser]);
  const { spacing } = useTheme();
  const toggleOpen = useCallback(() => setOpen(open => !open), [setOpen]);
  const drawerRef = useRef<HTMLUListElement>(null);
  const [drawerFullWidth, setDrawerFullWidth] = useState(navbar.defaultFullWidth);

  useEffect(() => {
    if (drawerRef.current) {
      setDrawerFullWidth(drawerRef.current.offsetWidth);
    }
  }, [i18n.language, isNavbarOpen]);

  const cssVariables = useMemo(
    () => [
      {
        key: NAVBAR_FULL_WIDTH_CSS_VAR,
        value: `${drawerFullWidth}px`
      }
    ],
    [drawerFullWidth]
  );

  useCssVariables(cssVariables);
  useBodyClass([isNavbarOpen ? 'Navbar-open' : 'Navbar-closed']);

  return (
    <Drawer
      sx={{
        width: isNavbarOpen ? `var(${NAVBAR_FULL_WIDTH_CSS_VAR})` : navbar.collapsedWidth,
        '& .MuiDrawer-paper': {
          width: isNavbarOpen ? `var(${NAVBAR_FULL_WIDTH_CSS_VAR})` : navbar.collapsedWidth
        },
        '& .MuiListItemIcon-root': applyDirectionalStyles(0, 1, 'ml', 'mr'),
        color: 'text.primary'
      }}
      PaperProps={{
        component: 'nav'
      }}
      variant="permanent"
      anchor={start}
    >
      <CollapseButton onClick={toggleOpen} isOpen={isNavbarOpen} sx={{ [end]: 0 }} />
      <Box sx={{ overflow: 'hidden', width: '100%' }}>
        <List sx={{ width: 'fit-content' }} ref={drawerRef}>
          {menuItems.map(({ title, Icon, to }) => (
            <ListItem
              key={title}
              sx={{
                p: 0
              }}
            >
              <ListItemButton
                key={title}
                component={NavLink}
                to={to}
                sx={{
                  p: spacing(1, 3),
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': {
                    fontWeight: typography.fontWeightMedium,
                    color: 'primary.main',
                    bgcolor: 'background.default'
                  },
                  '&.active': {
                    fontWeight: typography.fontWeightMedium,
                    color: 'primary.main'
                  }
                }}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  secondary={title}
                  secondaryTypographyProps={
                    {
                      'data-text': title
                    } as React.HTMLAttributes<HTMLElement>
                  }
                  sx={{
                    textAlign: start,
                    opacity: isNavbarOpen ? 1 : 0,
                    transition: transitions.opacity,
                    whiteSpace: 'nowrap',
                    '& .MuiTypography-root': {
                      color: 'inherit',
                      fontWeight: 'inherit',
                      textDecoration: 'inherit'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Navbar;
